/* @flow */

import { either } from 'ramda';
import { hasURLProtocol } from '@e180/util/lib/url';
import { getEventLoginURL } from '@e180/domain/lib/event/util';
import { isMembershipOnboardingCompleted } from '@e180/domain/lib/membership/util';

import { getRoutePath } from './routeUtils';
import {
  homeRoute,
  signupRoute,
  loginRoute,
} from 'src/shared/app/base/route/setting/routeSettings';
import {
  getInitialUrl,
  getAuthenticationToken,
} from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';
import { getKlikProfileURL } from 'plugin/klik/util/klikUtils';
import { isOnboardingCompletedOnDevice } from 'src/shared/app/onboarding/selector/uiOnboardingSelectors';
import { isOptinDone } from 'src/shared/app/permission/util/permissionUtils';
import { isSignupCompleted } from 'src/shared/app/signup/selector/uiSignupSelector';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { getMembership } from 'src/shared/domain/membership/selector/membershipSelectors';
import { getSelf } from 'src/shared/domain/user/selector/userSelectors';

import type { State } from 'src/shared/core/type/reduxTypes';

export function standardAuthenticatedPageRedirect(
  state: State,
  config: Object,
): ?string {
  return either(
    either(redirectToLoginUnlessAuthenticated, redirectToSignupUnlessCompleted),
    redirecToHomeUnlessOnboardingAndOptinCompleted,
  )(state, config);
}

export function redirectToLoginUnlessAuthenticated(state: State): ?string {
  if (!getAuthenticationToken(state)) {
    return getRoutePath(loginRoute);
  }
}

export function redirectToSignupUnlessCompleted(
  state: State,
  { isSignupEnabled }: Object,
): ?string {
  if (isSignupEnabled) {
    const user = getSelf(state);

    if (user && !isSignupCompleted(state, user)) {
      return getRoutePath(signupRoute);
    }
  }
}

export function redirecToHomeUnlessOnboardingAndOptinCompleted(
  state: State,
  { areEmailNotificationsEnabled }: Object,
): ?string {
  const membership = getMembership(state);

  if (
    membership &&
    (!isMembershipOnboardingCompleted(membership) ||
      !isOptinDone(membership, areEmailNotificationsEnabled))
  ) {
    return getRoutePath(homeRoute);
  }
}

export function redirectWhenRedirectAndAuthenticated(state: State): ?string {
  const initialURL = getInitialUrl(state);

  if (initialURL && getAuthenticationToken(state)) {
    return initialURL;
  }
}

export function redirectToLoginIfOnboardingCompletedOnDevice(
  state: State,
): ?string {
  if (!getAuthenticationToken(state) && isOnboardingCompletedOnDevice(state)) {
    return getRoutePath(loginRoute);
  }
}

export function redirectToHomeWhenSignupDisabled(
  state: State,
  { isSignupEnabled }: Object,
): ?string {
  if (!isSignupEnabled) {
    return getRoutePath(homeRoute);
  }
}

export function redirectToHomeWhenSignupCompleted(state: State): ?string {
  const user = getSelf(state);

  if (user && isSignupCompleted(state, user)) {
    return getRoutePath(homeRoute);
  }
}

export function redirectToHomeWhenAuthenticated(state: State): ?string {
  if (getAuthenticationToken(state)) {
    return getRoutePath(homeRoute);
  }
}

export function redirectToLoginURL(state: State): ?string {
  const loginURL = getEventLoginURL(getEventSafely(state));

  if (loginURL && hasURLProtocol(loginURL)) {
    return loginURL;
  }
}

export function integrationCustomRedirect(state: State): ?string {
  return getKlikProfileURL(getEventSafely(state));
}

export function standardUnauthenticatedPageRedirect(state: State): ?string {
  return redirectToHomeWhenAuthenticated(state);
}
